import NiceModal from "@ebay/nice-modal-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { GetDPSDetailResponse } from "@repo/api";
import {
    currentStepIndexSelector,
    insertStepsIfMissing,
    removeStepIfPresent,
    resetQuestionnaire,
    setExistingFunds,
    setNewFunds,
    setStepByName,
    setWhatToChangeAnswers,
    StepperIndexes,
    StepperType,
    stepperTypeSelector,
    useAppDispatch,
    useAppSelector,
    whatToChangeAnswersSelector,
} from "@repo/logic";
import DOMPurify from "dompurify";
import { FC, forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useController, useForm } from "react-hook-form";
import * as Yup from "yup";
import { FormErrorMessage } from "../..";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { SwitchInput } from "../Form";
import { ProgressBarList } from "../ProgressBarList";
import { DefaultStepProps } from "../Stepper";
import { BodyTextM, HeadingM } from "../Typography";
import { PensionQuestionnaireStepProps } from "./PensionQuestionnaireStepper";
import * as Styled from "./PensionQuestionnaireWhatToChange.styled";
import { mapCurrentAllocationsFromDetail } from "./Questionnaire/QuestionnairePage.helpers";

type FormValues = {
    existingContribution: boolean | null;
    newContribution: boolean | null;
    atLeastOne: boolean;
};

export const PensionQuestionnaireWhatToChange: FC<DefaultStepProps & PensionQuestionnaireStepProps> = ({
    setContinueCondition,
    queries: { questionnaireConfigQuery, usePensionDetail } = {},
    contractId,
}) => {
    const { t } = useTypedTranslation();
    const dispatch = useAppDispatch();
    const currentPageIndex = useAppSelector(currentStepIndexSelector);
    const savedValues = useAppSelector(whatToChangeAnswersSelector);
    const stepperType = useAppSelector(stepperTypeSelector);
    const [noChangeConfirmed, setNoChangeConfirmed] = useState(false);
    const errorRef = useRef<HTMLDivElement | null>(null);

    const { data: pensionDetail, refetch: refetchPensionDetail } =
        usePensionDetail?.({ id: String(contractId), options: { enabled: Boolean(contractId) }, type: "UF" }) ?? {};

    const { data: config, refetch: refetchQuestionnaireConfig } = questionnaireConfigQuery ?? {};

    useEffect(() => {
        if (!config) refetchQuestionnaireConfig?.();
    }, [refetchQuestionnaireConfig, config]);

    useEffect(() => {
        if (!pensionDetail) {
            refetchPensionDetail?.();
        }
    }, [pensionDetail, refetchPensionDetail]);

    const currentAllocationsData = useMemo(() => {
        const existingData = (pensionDetail as GetDPSDetailResponse | undefined)?.funds
            ? mapCurrentAllocationsFromDetail(pensionDetail as GetDPSDetailResponse, config, "existing")
            : undefined;
        const newData = (pensionDetail as GetDPSDetailResponse | undefined)?.funds
            ? mapCurrentAllocationsFromDetail(pensionDetail as GetDPSDetailResponse, config, "new")
            : undefined;

        const existingRows = existingData?.currentAllocations.map((allocation) => {
            const fund = config?.funds.find((f) => f.id === allocation.fundId);
            return {
                id: allocation.fundId,
                label: fund?.name ?? "",
                value: allocation.percentage,
            };
        });

        const newRows = newData?.currentAllocations.map((allocation) => {
            const fund = config?.funds.find((f) => f.id === allocation.fundId);
            return {
                id: allocation.fundId,
                label: fund?.name ?? "",
                value: allocation.percentage,
            };
        });

        return {
            existingRows,
            newRows,
            savings: existingData?.savings,
        };
    }, [pensionDetail, config]);

    const sanitizedHtmlTexts = useMemo(() => {
        const getDescriptionKey = (type?: StepperType) => {
            if (type === StepperType.ChangePensionContribution || type === StepperType.PensionQuestionnaire) {
                return "investmentStrategy.whatToChange.descriptionForChangeContribution";
            }
            return "investmentStrategy.whatToChange.description";
        };
        const descriptionKey = getDescriptionKey(stepperType);
        const description = DOMPurify.sanitize(t(descriptionKey), {
            ALLOWED_TAGS: ["strong"],
        });
        const error = DOMPurify.sanitize(t("investmentStrategy.whatToChange.error"), {
            ALLOWED_TAGS: ["strong"],
        });
        return { description, error };
    }, [t, stepperType]);

    const getAtLeastOneErrorMessage = () => {
        if (stepperType === StepperType.PensionQuestionnaire && !noChangeConfirmed) {
            return t("investmentStrategy.whatToChange.noChangeWarning");
        }
        if (stepperType === StepperType.ChangePensionContribution && !noChangeConfirmed) {
            return t("investmentStrategy.whatToChange.noChangeWarningForChangeContribution");
        }
        return sanitizedHtmlTexts.error;
    };

    const schema = Yup.object().shape({
        existingContribution: Yup.boolean().required().nullable(),
        newContribution: Yup.boolean().required().nullable(),
        //one of them must be true
        atLeastOne: Yup.boolean()
            .required()
            .test({
                name: "atLeastOne",
                message: getAtLeastOneErrorMessage(),
                test: function (_, ctx) {
                    const { existingContribution, newContribution } = ctx.parent;
                    const hasAnyTrue = Boolean(existingContribution) || Boolean(newContribution);
                    const bothFalse = existingContribution === false && newContribution === false;

                    // Pro PensionQuestionnaire stepper: pokud jsou obě false
                    if (stepperType === StepperType.PensionQuestionnaire && bothFalse) {
                        // Při prvním pokusu zobrazit varování
                        if (!noChangeConfirmed) {
                            return false;
                        }
                        // Při druhém pokusu povolit pokračovat (zavře se stepper)
                        return true;
                    }

                    // Pro ChangePensionContribution: pokud jsou obě false
                    if (stepperType === StepperType.ChangePensionContribution && bothFalse) {
                        if (!noChangeConfirmed) {
                            return false;
                        }
                        return true;
                    }

                    // Pro ostatní steppery nebo když je vybrána alespoň jedna možnost
                    return hasAnyTrue;
                },
            }),
    });

    const methods = useForm<FormValues>({
        defaultValues: {
            existingContribution: null,
            newContribution: null,
            atLeastOne: false,
        },
        mode: "onSubmit",
        resolver: yupResolver(schema),
    });

    const {
        trigger,
        watch,
        clearErrors,
        formState: { isDirty },
        getValues,
    } = methods;

    const [existingContribution, newContribution] = watch(["existingContribution", "newContribution"]);

    useEffect(() => {
        if (existingContribution === true || newContribution === true) {
            setNoChangeConfirmed(false);
        }
    }, [existingContribution, newContribution]);

    useEffect(() => {
        // set saved values if any
        if (savedValues?.existingContribution === true) {
            methods.setValue("existingContribution", savedValues.existingContribution);
        }
        if (savedValues?.newContribution === true) {
            methods.setValue("newContribution", savedValues.newContribution);
        }
    }, [methods, savedValues]);

    useEffect(() => {
        // set validation condition for the stepper to be able to continue
        setContinueCondition(async () => {
            const isValid = await trigger();
            const bothFalse = existingContribution !== true && newContribution !== true;

            // Pro PensionQuestionnaire: pokud jsou obě false
            if (stepperType === StepperType.PensionQuestionnaire && bothFalse) {
                // Pokud už byla hlaška zobrazena a validace znovu vrátí false (druhý pokus)
                if (noChangeConfirmed && !isValid) {
                    // Zavřít modální okno a resetovat stepper
                    NiceModal.hide("Nicemodal-PensionQuestionnaireStepperModal");
                    dispatch(resetQuestionnaire());
                    return true;
                }

                // Při prvním pokusu (noChangeConfirmed je false)
                if (!noChangeConfirmed && !isValid) {
                    // Scroll to error message
                    errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    // Nastavit příznak, že uživatel byl upozorněn s mírným spožděním (pro lidi co používají double click)
                    setTimeout(() => setNoChangeConfirmed(true), 500);
                    return false;
                }

                // Pokud validace prošla (nejspíš už uživatel změnil hodnoty)
                if (isValid) {
                    return true;
                }
            }

            // Pro ChangePensionContribution: pokud jsou obě false
            if (stepperType === StepperType.ChangePensionContribution && bothFalse) {
                if (!noChangeConfirmed && !isValid) {
                    errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                    setTimeout(() => setNoChangeConfirmed(true), 500);
                    return false;
                }

                if (isValid || (noChangeConfirmed && !isValid)) {
                    dispatch(
                        setWhatToChangeAnswers({
                            existingContribution: false,
                            newContribution: false,
                        }),
                    );
                    dispatch(setExistingFunds({ existingFundRows: undefined }));
                    dispatch(setNewFunds({ newFundRows: undefined }));
                    dispatch(setStepByName(StepperIndexes.changeContributionRecapPage));
                    return false;
                }
            }

            if (isValid) {
                //save to redux
                dispatch(
                    setWhatToChangeAnswers({
                        existingContribution: existingContribution ?? false,
                        newContribution: newContribution ?? false,
                    }),
                );
                // clear funds if the user has deselected the options
                if (existingContribution !== true) {
                    dispatch(setExistingFunds({ existingFundRows: undefined }));
                }
                if (newContribution !== true) {
                    dispatch(setNewFunds({ newFundRows: undefined }));
                }
            }
            return isValid;
        });
    }, [
        dispatch,
        getValues,
        setContinueCondition,
        trigger,
        stepperType,
        noChangeConfirmed,
        existingContribution,
        newContribution,
    ]);

    useEffect(() => {
        // clear validation errors when user changes switch values
        if (isDirty) clearErrors("atLeastOne");
        // add or remove steps based on the selected options
        if (!existingContribution) dispatch(removeStepIfPresent(StepperIndexes.editExistingInvestmentStrategyPage));
        if (!newContribution) dispatch(removeStepIfPresent(StepperIndexes.editNewInvestmentStrategyPage));
        if (existingContribution)
            dispatch(
                insertStepsIfMissing({
                    steps: [StepperIndexes.editExistingInvestmentStrategyPage],
                    index: currentPageIndex + 1,
                }),
            );
        if (newContribution)
            dispatch(
                insertStepsIfMissing({
                    steps: [StepperIndexes.editNewInvestmentStrategyPage],
                    index: currentPageIndex + 1 + (existingContribution ? 1 : 0),
                }),
            );
    }, [clearErrors, existingContribution, newContribution, dispatch, currentPageIndex, isDirty]);

    return (
        <Styled.Wrapper>
            {/* Current allocation section */}
            {(currentAllocationsData.existingRows || currentAllocationsData.newRows) && (
                <Styled.CurrentAllocationSection>
                    <Styled.CurrentAllocationTitle>
                        {t("investmentStrategy.whatToChange.currentAllocationTitle")}
                    </Styled.CurrentAllocationTitle>
                    <Styled.AllocationGrid>
                        <Styled.AllocationColumn>
                            <Styled.AllocationColumnHeader>
                                <Styled.AllocationColumnTitle>
                                    {t("investmentStrategy.whatToChange.existingResources")}
                                </Styled.AllocationColumnTitle>
                                {currentAllocationsData.savings != null && (
                                    <Styled.SavingsInfo>
                                        <span>{t("investmentStrategy.whatToChange.savings")}</span>
                                        <span>
                                            {currentAllocationsData.savings.toLocaleString("cs-CZ", {
                                                style: "currency",
                                                currency: "CZK",
                                            })}
                                        </span>
                                    </Styled.SavingsInfo>
                                )}
                            </Styled.AllocationColumnHeader>
                            <ProgressBarList
                                rows={currentAllocationsData.existingRows}
                                isLoading={
                                    !currentAllocationsData.existingRows ||
                                    currentAllocationsData.existingRows.length === 0
                                }
                                loadingRowsCount={3}
                            />
                        </Styled.AllocationColumn>
                        <Styled.AllocationColumn>
                            <Styled.AllocationColumnTitleWrapper>
                                <Styled.AllocationColumnTitle>
                                    {t("investmentStrategy.whatToChange.newContributions")}
                                </Styled.AllocationColumnTitle>
                            </Styled.AllocationColumnTitleWrapper>
                            <ProgressBarList
                                rows={currentAllocationsData.newRows}
                                isLoading={
                                    !currentAllocationsData.newRows || currentAllocationsData.newRows.length === 0
                                }
                                loadingRowsCount={3}
                            />
                        </Styled.AllocationColumn>
                    </Styled.AllocationGrid>
                </Styled.CurrentAllocationSection>
            )}

            <FormProvider {...methods}>
                <Styled.FullWidthForm>
                    <Styled.Wrapper>
                        <Styled.Title>{t("investmentStrategy.whatToChange.title")}</Styled.Title>
                        <Styled.Paragraph
                            dangerouslySetInnerHTML={{ __html: sanitizedHtmlTexts.description }}
                        ></Styled.Paragraph>
                        <Styled.WhatToChangeCardRow>
                            <Styled.Card>
                                <HeadingM>{t("investmentStrategy.existingResources.title")}</HeadingM>
                                <BodyTextM>{t("investmentStrategy.existingResources.label")}</BodyTextM>
                                <SwitchInput name="existingContribution" label={t("global.change")} />
                            </Styled.Card>
                            <Styled.Card>
                                <HeadingM>{t("investmentStrategy.newResources.title")}</HeadingM>
                                <BodyTextM>{t("investmentStrategy.newResources.label")}</BodyTextM>
                                <SwitchInput name="newContribution" label={t("global.change")} />
                            </Styled.Card>
                        </Styled.WhatToChangeCardRow>
                        <AlLeastOneError ref={errorRef} />
                    </Styled.Wrapper>
                </Styled.FullWidthForm>
            </FormProvider>
        </Styled.Wrapper>
    );
};

const AlLeastOneError = forwardRef<HTMLDivElement>((_, ref) => {
    const { fieldState } = useController<FormValues>({ name: "atLeastOne" });

    return (
        <Styled.AlLeastOneErrorWrapper ref={ref}>
            {fieldState.error && (
                <FormErrorMessage name="atLeastOne" severity="error">
                    <BodyTextM dangerouslySetInnerHTML={{ __html: fieldState.error.message ?? "" }}></BodyTextM>
                </FormErrorMessage>
            )}
        </Styled.AlLeastOneErrorWrapper>
    );
});
